import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { getErrorMessage } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import FileIcon from "@entrilia/shared/components/FileIcon";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { formatFileSize, getFileExtension, readFileAsText } from "@entrilia/shared/utilities/fileHelper";
import { fileToUploadValidator } from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";

const acceptedFileExtensions = [".docx", ".doc", ".html"];
export const maxFileSize = 1_000_000; // 1 MB

const validateFile = fileToUploadValidator({ maxFileSize, acceptedFileExtensions });

interface Props {
  onClose: () => void;
  onImport: (htmlContent: string) => void;
}

const ImportConsentFileDialog = ({ onClose, onImport }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [converting, setConverting] = useState(false);
  const [file, setFile] = useState<File>();
  const [fileError, setFileError] = useState<string>();

  const handleAddFiles = (files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }

    const validationResult = validateFile(file);
    setFileError(validationResult.error);
    setFile(file);
  };

  const handleDeleteFile = () => {
    setFile(undefined);
    setFileError(undefined);
  };

  const handleImport = async () => {
    if (file === undefined) {
      return;
    }

    if (getFileExtension(file.name) === ".html") {
      try {
        const htmlContent = await readFileAsText(file);
        onImport(htmlContent);
        onClose();
        return;
      } catch (error) {
        sendNotificationError(getErrorMessage(error));
        return;
      }
    }

    try {
      setConverting(true);
      const convertedHtmlContent = await adminApi.convertUserConsentContentFileToHtml(file);
      setConverting(false);

      if (!convertedHtmlContent) {
        sendNotificationError("Failed to convert file to HTML");
        return;
      }

      onImport(convertedHtmlContent);
      onClose();
    } catch (error) {
      setConverting(false);
      sendNotificationError(`Failed to convert file to HTML: ${getErrorMessage(error)}`);
      logError(error, "[ImportConsentFileDialog] convertUserConsentContentFileToHtml");
      return;
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>Import File</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={3} sx={{ height: "24rem" }}>
          <Alert severity="info">After importing the file, editing in the content editor will not be available.</Alert>

          {file === undefined && (
            <FilesDropArea
              acceptedFileExtensions={acceptedFileExtensions}
              subtitle="Supported formats: html, doc, docx. Content may not contain images or drawings."
              onFilesAdd={handleAddFiles}
              containerProps={{ sx: { flex: 1 } }}
            />
          )}

          {file !== undefined && (
            <TableContainer component={Paper} variant="outlined" sx={{ width: "100%" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ width: 100 }}>Size</TableCell>
                    <TableCell sx={{ width: 64 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <FileIcon fileExtension={getFileExtension(file.name)} />
                        <Typography noWrap>{file.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{formatFileSize(file.size)}</TableCell>
                    <TableCell>
                      <IconButton onClick={handleDeleteFile}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!!fileError && <FormHelperText error>{fileError}</FormHelperText>}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={converting}>
        <Button
          variant="contained"
          loading={converting}
          onClick={handleImport}
          disabled={file === undefined || !!fileError}
        >
          Import File
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default ImportConsentFileDialog;
